import styled from 'styled-components';
import { MONTH_PASCAL } from '../../constants';
import { useModal } from '../../context/ModalContext';
import {
  updateBaseCPM,
  updateBaseCPT
} from '../../redux/features/calculated/calculatedSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { RATECARD, getBasePrice } from '../../services/calculate';
import {
  getMonthsBetweenDates,
  mapPascalMonthToLowercaseMonth
} from '../../util';
import { BottomNavigation } from '../BottomNavigation';
import { ContentContainer } from '../containers';
import CampaignDetails from './CampaignDetails';
import CampaignDurations from './CampaignDurations';
import CampaignLength from './CampaignLength';
import TargetAudience from './TargetAudience';
import CommencingDaySelector from './CommencingDaySelector';

const StyledDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const DetailsSection = () => {
  const dispatch = useAppDispatch();
  const { closeModal, openModal } = useModal();

  const { pricing } = useAppSelector((state: RootState) => state.spreadsheet);
  const {
    agencyName,
    advertiserDiscount,
    advertiserName,
    campaignName,
    demographic,
    campaignDurations
  } = useAppSelector((state: RootState) => state.detail);
  const { startDate, endDate } = useAppSelector(
    (state: RootState) => state.detail.campaignLength
  );

  const handleOnClick = (): boolean => {
    return validateFields();
  };

  const validateFields = (): boolean => {
    let isError = false;

    if (!agencyName) {
      showModal('Please enter an Agency Name to continue.');
      isError = true;
    }

    if (!advertiserName) {
      showModal('Please enter an Advertiser Name to continue.');
      isError = true;
    }

    if (!campaignName) {
      showModal('Please enter a Campaign Name to continue.');
      isError = true;
    }

    if (!startDate) {
      showModal('Please select a start date to continue.');
      isError = true;
    }

    if (!endDate) {
      showModal('Please select an end date to continue.');
      isError = true;
    }

    if (!demographic) {
      showModal('Please select a demographic to continue.');
      isError = true;
    }

    if (!Object.values(campaignDurations).some((val) => val === true)) {
      showModal('Please select at least one campaign duration to continue.');
      isError = true;
    }

    if (!isError) {
      saveBaseValuesToRedux();
    }

    return isError;
  };

  const showModal = (content: string): void => {
    openModal('ActionModal', {
      title: 'Error',
      content,
      cancelButtonText: 'Cancel',
      okButtonText: 'OK',
      modalCallback
    });
  };

  const modalCallback = () => {
    closeModal();
  };

  const saveBaseValuesToRedux = (): void => {
    const months = getMonths();
    months.map((month) => {
      const lowercaseMonth = mapPascalMonthToLowercaseMonth(
        month as MONTH_PASCAL
      );

      const baseCPT = getBaseCPT(month as MONTH_PASCAL);
      const baseCPM = getBaseCPM(month as MONTH_PASCAL);

      if (baseCPT === undefined || baseCPM === undefined) {
        return;
      }

      dispatch(updateBaseCPT({ baseCPT, month: lowercaseMonth }));
      dispatch(updateBaseCPM({ baseCPM, month: lowercaseMonth }));
    });
  };

  const getBaseCPT = (month: MONTH_PASCAL): number | undefined => {
    if (!pricing || !demographic) {
      return undefined;
    }

    return getBasePrice(pricing, {
      demographic,
      ratecard: RATECARD.CPT,
      month,
      discount: advertiserDiscount
    });
  };

  const getBaseCPM = (month: MONTH_PASCAL): number | undefined => {
    if (!pricing || !demographic) {
      return undefined;
    }

    return getBasePrice(pricing, {
      demographic,
      ratecard: RATECARD.CPM,
      month,
      discount: advertiserDiscount
    });
  };

  const getMonths = (): string[] => {
    if (!startDate || !endDate) {
      return [];
    }

    return getMonthsBetweenDates(startDate, endDate);
  };

  return (
    <>
      <ContentContainer>
        <h2>Details</h2>
        <CampaignDetails />
      </ContentContainer>
      <ContentContainer>
        <h2>Details</h2>
        <StyledDetails>
          <CampaignDurations />
          <CampaignLength />
          <CommencingDaySelector />
          <TargetAudience />
        </StyledDetails>
      </ContentContainer>
      <BottomNavigation
        nextTo="/second"
        onClick={handleOnClick}
        showBackNavigation={false}
      />
    </>
  );
};

export default DetailsSection;
